import {colors, Switch} from '@material-ui/core';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {SvgIconProps} from '@material-ui/core/SvgIcon';
import Typography from '@material-ui/core/Typography';
import CostumeIcon from '@material-ui/icons/AccessibilityNewOutlined';
import ShoeIcon from '@material-ui/icons/AirlineSeatLegroomExtraOutlined';
import HotelIcon from '@material-ui/icons/Apartment';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import TicketIcon from '@material-ui/icons/ConfirmationNumberOutlined';
import EventIcon from '@material-ui/icons/EventOutlined';
import TourIcon from '@material-ui/icons/Flight';
import FolderIcon from '@material-ui/icons/FolderOpenOutlined';
import RoomIcon from '@material-ui/icons/Hotel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import TreeItem, {TreeItemProps} from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import React, {useEffect, useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Resource, ResourceOption, ResourceType} from '../../../graphql/resources.datatypes';
import OptionIcon from '../../../icons/Cogs'
import getResource from '../getResource.api';

type StyledTreeItemProps = TreeItemProps & {
  labelIcon?: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
};

const useTreeItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelRoot: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
      color: colors.blueGrey[900],
    },
    labelIcon: {
      marginRight: theme.spacing(1),
      width: 18,
      height: 18,
    },
    labelText: {
      fontWeight: 'inherit',
      flexGrow: 1,
    },
  }),
);

const ResourceTreeItem = (props: {resourceItem: Resource, onResourceView?: (resource: Resource) => void, hideView: boolean} & StyledTreeItemProps) => {
  const classes = useTreeItemStyles();
  const { labelText, labelIcon: LabelIcon, labelInfo, resourceItem, onResourceView, hideView, ...other } = props;

  let Icon;
  switch(resourceItem.resource_type) {
    case ResourceType.TOUR: {
      Icon = TourIcon;
      break;
    }
    case ResourceType.HOTEL: {
      Icon = HotelIcon;
      break;
    }
    case ResourceType.ROOM: {
      Icon = RoomIcon;
      break;
    }
    case ResourceType.COSTUME: {
      Icon = CostumeIcon;
      break;
    }
    case ResourceType.SHOES: {
      Icon = ShoeIcon;
      break;
    }
    case ResourceType.SEASON: {
      Icon = EventIcon;
      break;
    }
    case ResourceType.ROOM_BANK: {
      Icon = RoomIcon;
      break;
    }
    case ResourceType.FOLDER: {
      Icon = FolderIcon;
      break;
    }
    default: {
      Icon = TicketIcon;
      break;
    }
  }

  return (
    <TreeItem
      label={
        <div className={classes.labelRoot} >
          <Icon color="inherit" className={classes.labelIcon} />
          <Typography variant="body2" className={classes.labelText}>
            {labelText}
          </Typography>
          {!hideView && (
            <Typography  color="inherit"  onClick={event => {
              event.stopPropagation();
              if(onResourceView) onResourceView(resourceItem);
            }}>
              view
            </Typography>
          )}
        </div>
      }
      {...other}
    />
  );
};

const ResourceOptionTreeItem = (props: { resourceOption: ResourceOption, onResourceOptionClick?: (resourceOption: ResourceOption, selected?: boolean) => void, isOptionsTree: boolean, isSelected: boolean } & StyledTreeItemProps) => {
  const classes = useTreeItemStyles();
  const {labelText, labelIcon: LabelIcon, labelInfo, resourceOption, onResourceOptionClick, isOptionsTree, isSelected, ...other} = props;

  return (
    <TreeItem
      label={
        <div className={classes.labelRoot}>
          <OptionIcon color="inherit" className={classes.labelIcon}/>
          <Typography variant="body2" className={classes.labelText}>
            {labelText}
          </Typography>
          {isOptionsTree && (
            <Switch
              checked={isSelected}
              color="secondary"
              edge="start"
              name="disabled"
              size="small"
              onChange={() => {
                if (onResourceOptionClick) onResourceOptionClick(resourceOption, !isSelected)
              }}
              value={isSelected}
            />
          )}
          {!isOptionsTree && (
            <Typography variant="caption" color="inherit" onClick={event => {
              event.stopPropagation();
              if(onResourceOptionClick) onResourceOptionClick(resourceOption);
            }}>
              view
            </Typography>
          )}

        </div>
      }
      {...other}
    />
  );
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

const ResourcesTree = (props: {
  resources: Resource[],
  hideViewIds?: number[],
  optionsTourId?: number,
  optionsHotelId?: number,
  onResourceView?: (resource: Resource) => void,
  onResourceOptionClick?: (resourceOption: ResourceOption, selected?: boolean) => void,
  selectedOptionIds?: number[],
}) => {
  const classes = useStyles();
  const isOptionsTree = () => {
    return !!(optionsTourId || optionsHotelId);

  };
  const [resources, setResources] = useState<Resource[]>([...props.resources]);
  const [loadedIds, setLoadedIds] = useState<number[]>([]);
  const [loadingId, setLoadingId] = useState<number | null>( null);
  const resourcesData = getResource(loadingId);
  const {optionsTourId, optionsHotelId, selectedOptionIds} = props;

  //This hack forces the tree component setState items to cause a full change of the <Tree /> component not just a rerender.
  const [isRendering, setIsRendering] = useState(false);
  useEffect(() => {
    setIsRendering(false);
    const timeout = setTimeout(()=>{
      setResources([...props.resources]);
      setLoadedIds([]);
      setIsRendering(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, [props.resources]);

  useEffect(() => {
    if(resourcesData.data && resourcesData.data.resource) {
      const resource = resourcesData.data.resource;

      const iterateReplace = (checkResources: Resource[]): Resource[] => {
        return checkResources.map( checkResource => {
          if(checkResource.id === resource.id) return resource;
          if(checkResource.resources) return {...checkResource, resources: iterateReplace(checkResource.resources) as Resource[]};
          return checkResource;
        });
      };

      setResources(iterateReplace(resources));
      setLoadedIds([...loadedIds, resource.id]);
    }
  }, [resourcesData.data]);

  const renderResourceOptionTreeItem = (resourceOption: ResourceOption) => {
    return (
      <ResourceOptionTreeItem
        key={'resrouce_option_' + resourceOption.id}
        nodeId={'resrouce_option_' + resourceOption.id}
        labelText={resourceOption.internal_name!}
        resourceOption={resourceOption}
        onResourceOptionClick={props.onResourceOptionClick}
        isOptionsTree={isOptionsTree()}
        isSelected={selectedOptionIds ? !!selectedOptionIds.find(item => item === resourceOption.id) : false}
      />
    );
  };

  const renderTreeItem = (resource: Resource) => {

    let children: any[] = [];
    const resourceLoaded = (loadedIds.indexOf(resource.id) > -1);

    const disableNode = () => {
      if(optionsTourId && resource.resource_type === ResourceType.TOUR && resource.id !== optionsTourId) return true;
      if(optionsHotelId && resource.resource_type === ResourceType.HOTEL && resource.id !== optionsHotelId) return true;
      return false;
    };

    if(disableNode()) {
      return null;
    }

    if(resource.resources){
      children = children.concat(resource.resources.map(renderTreeItem));
    }

    if (resource.options) {
      children = children.concat(resource.options.map(renderResourceOptionTreeItem));
    }

    children = children.filter(item => !!item);

    const icons = {
      collapseIcon: children.length > 0? (<ArrowDropDownIcon />) : resourceLoaded? null : (<HourglassEmptyIcon />),
      expandIcon: children.length > 0? (<ArrowRightIcon />) : resourceLoaded? null : (<KeyboardArrowRightIcon />),
    };

    const onClick = () => {
      if(!resourceLoaded && !disableNode()) setLoadingId(resource.id);
    };

    const hideView = (!!optionsTourId) || (!!optionsHotelId) || (props.hideViewIds? props.hideViewIds.indexOf(resource.id) > -1 : false);

    return (
      <ResourceTreeItem
        key={'resource_'+resource.id}
        nodeId={'resource_'+resource.id}
        labelText={resource.internal_name}
        children={children} {...icons}
        onClick={() => onClick()}
        resourceItem={resource}
        onResourceView={props.onResourceView}
        hideView={hideView}
      />
    );
  };

  return isRendering ? (
    <TreeView className={classes.root} defaultExpanded={resources.length > 1? [] : resources.map(item => 'resource_'+item.id)}>
      <LoadingOverlay loading={!!loadingId && resourcesData.loading}>
        {resources.map(renderTreeItem).filter(item => !!item)}
        {resources.length === 0 && (
          <Typography>No resources found.</Typography>
        )}
      </LoadingOverlay>
    </TreeView>
  ) : null;
};

export default ResourcesTree;
